import { Link, useNavigate } from 'react-router-dom';
import { MapPin, Briefcase, Clock, ShieldCheck, AlertTriangle, BadgeCheck } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { AuraButton } from '@/components/aura';
import type { DirectoryAppraiser, DirectoryLicense } from '@/hooks/useDirectoryAppraisers';
import { getDesignations } from '@/hooks/useDirectoryAppraisers';
import { isLicenseExpired } from '@/lib/licenseUtils';

function getPrimaryLicense(licenses: DirectoryLicense[]): DirectoryLicense | null {
  return licenses.find(l => l.is_primary) || licenses[0] || null;
}

/** Drop the redundant "Real Estate Appraiser" suffix — it's obvious on an appraisal site. */
function shortLicenseType(type?: string | null): string {
  if (!type) return '';
  return type.replace(/\s*(real estate )?appraiser\s*$/i, '').trim() || type;
}

interface DirectoryCardProps {
  appraiser: DirectoryAppraiser;
}

/** Soft pill chip — the aura register for small metadata labels. */
function Tag({ children, tone = 'line' }: { children: React.ReactNode; tone?: 'line' | 'accent' | 'tint' | 'warn' }) {
  const styles: React.CSSProperties =
    tone === 'accent'
      ? { background: 'var(--g5-accent)', color: 'var(--g5-on-accent)' }
      : tone === 'tint'
      ? { background: 'var(--g5-tint)', color: 'var(--g5-accent)' }
      : tone === 'warn'
      ? { border: '1px solid hsl(var(--destructive) / 0.45)', color: 'hsl(var(--destructive))' }
      : { border: '1px solid var(--g5-line)', color: 'var(--g5-sub)' };
  return (
    <span
      className="inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[0.65rem] font-semibold leading-[1.4]"
      style={styles}
    >
      {children}
    </span>
  );
}

function MetaRow({ icon: Icon, children }: { icon: React.ElementType; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-3.5 w-3.5 flex-shrink-0" style={{ color: 'var(--g5-accent)' }} />
      <span className="truncate">{children}</span>
    </div>
  );
}

export function DirectoryCard({ appraiser }: DirectoryCardProps) {
  const navigate = useNavigate();
  const initials = (appraiser.name || 'A')
    .split(' ')
    .map(n => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const designations = getDesignations(appraiser.certifications);
  const primaryLicense = getPrimaryLicense(appraiser.licenses);
  const profilePath = `/directory/${appraiser.slug || appraiser.id}`;
  const location = [appraiser.city, appraiser.state].filter(Boolean).join(', ');
  const company = appraiser.company_name || appraiser.brokerage_name;

  const currentYear = new Date().getFullYear();
  const years = appraiser.appraiser_since ? currentYear - appraiser.appraiser_since : appraiser.years_experience;
  const hasMeta = !!(company || location || (years != null && years > 0));

  return (
    <div
      className="aura-card group flex h-full flex-col rounded-3xl p-6"
      style={{ background: 'var(--g5-card)', border: '1px solid var(--g5-line)' }}
    >
      {/* ── Identity (fixed zone) ── */}
      <Link to={profilePath} className="flex items-start gap-4">
        <Avatar className="h-14 w-14 flex-shrink-0 rounded-2xl" style={{ border: '1px solid var(--g5-line)' }}>
          {appraiser.headshot_url ? <AvatarImage src={appraiser.headshot_url} alt={appraiser.name || 'Appraiser'} className="rounded-2xl" /> : null}
          <AvatarFallback className="rounded-2xl font-display text-base font-extrabold" style={{ background: 'var(--g5-tint)', color: 'var(--g5-accent)' }}>
            {initials}
          </AvatarFallback>
        </Avatar>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h3 className="truncate font-display text-base font-bold leading-tight tracking-tight transition-colors group-hover:text-[color:var(--g5-accent)]" style={{ color: 'var(--g5-ink)' }}>
              {appraiser.name || 'Appraiser'}
            </h3>
            {appraiser.is_verified && <ShieldCheck className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--g5-accent)' }} />}
          </div>
          {/* Reserved sub-block so the license row below always lands at the same height */}
          <div className="mt-1 min-h-[2.25rem]">
            {appraiser.title && <p className="truncate text-sm leading-snug" style={{ color: 'var(--g5-sub)' }}>{appraiser.title}</p>}
            {designations.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1 overflow-hidden" style={{ maxHeight: '1.5rem' }}>
                {designations.map(d => <Tag key={d} tone="accent">{d}</Tag>)}
              </div>
            )}
          </div>
        </div>
      </Link>

      {/* ── License (fixed zone) — credential line; truncates instead of overflowing ── */}
      <div className="mt-2 flex min-h-[1.6rem] items-center gap-2">
        {primaryLicense && (
          <>
            <BadgeCheck className="h-3.5 w-3.5 flex-shrink-0" style={{ color: 'var(--g5-accent)' }} strokeWidth={2} />
            <span className="min-w-0 flex-1 truncate text-xs font-semibold" style={{ color: 'var(--g5-ink)' }}>
              {shortLicenseType(primaryLicense.license_type)}
              <span style={{ color: 'var(--g5-sub)' }}> · {primaryLicense.license_state}</span>
            </span>
            {isLicenseExpired(primaryLicense.expiration_date) && (
              <span className="flex-shrink-0"><Tag tone="warn"><AlertTriangle className="h-2.5 w-2.5" />Expired</Tag></span>
            )}
          </>
        )}
      </div>

      {/* ── Meta (variable; the flexible middle absorbs its length) ── */}
      {hasMeta && (
        <div className="mt-3 space-y-2 border-t pt-4 text-sm" style={{ borderColor: 'var(--g5-line)', color: 'var(--g5-sub)' }}>
          {company && <MetaRow icon={Briefcase}>{company}</MetaRow>}
          {location && <MetaRow icon={MapPin}>{location}</MetaRow>}
          {years != null && years > 0 && <MetaRow icon={Clock}>{years}+ years experience</MetaRow>}
        </div>
      )}

      {/* flexible spacer — keeps the block below pinned to the card's bottom */}
      <div className="flex-1" />

      {/* ── Specialties (fixed zone, bottom-aligned) ── */}
      <div className="mt-4 flex min-h-[1.5rem] flex-wrap gap-1.5 overflow-hidden" style={{ maxHeight: '1.5rem' }}>
        {appraiser.specialties?.slice(0, 3).map(s => <Tag key={s} tone="tint">{s}</Tag>)}
        {appraiser.specialties && appraiser.specialties.length > 3 && <Tag tone="tint">+{appraiser.specialties.length - 3}</Tag>}
      </div>

      {/* ── Actions (always same place) ── */}
      <div className="mt-4 flex items-center gap-2">
        <AuraButton variant="soft" size="sm" className="flex-1" onClick={() => navigate(profilePath)}>
          View profile
        </AuraButton>
        <AuraButton size="sm" className="flex-1" onClick={() => navigate(`${profilePath}#contact`)}>
          Request quote
        </AuraButton>
      </div>
    </div>
  );
}
